/** Sentinel when auth has resolved but no usable user id is available. */
export const AUTH_SESSION_KEY_NONE = '__none__' as const

/** Suffix on {@link AuthSessionKey} for a real (non-anonymous) Better Auth account. */
export const AUTH_SESSION_KEY_ACCOUNT_SUFFIX = ':account' as const

/** Suffix on {@link AuthSessionKey} for an anonymous Better Auth guest session. */
export const AUTH_SESSION_KEY_ANON_SUFFIX = ':anon' as const

/** Session fingerprint for cache keys / SWR hydrate. */
export type AuthSessionKey = string

/**
 * Session key accepted by hydrate flows.
 * `null` while auth is pending; `'__none__'` when no usable user id is available.
 */
export type AuthHydrateSessionKey = AuthSessionKey | typeof AUTH_SESSION_KEY_NONE | null

export type UseAuthSessionKeyOptions = {
    /**
     * When true, anonymous Better Auth sessions resolve to `${userId}:anon`. When false (default),
     * only real signed-in accounts get a key; guest and anonymous sessions resolve to `'__none__'`.
     */
    includeAnonymous?: boolean
}

/** Minimal Better Auth session shape used to derive a cache key. */
export type AuthSessionSnapshot =
    | {
          isPending?: boolean
          data?: { user?: { id?: string; isAnonymous?: boolean } }
      }
    | null
    | undefined

/**
 * True when the session key is not yet ready (`null`) or has no usable identity
 * (`'__none__'`). Use to skip fetches / hydrate until a real `${userId}:…` key exists.
 */
export function isAbsentAuthSessionKey(
    key: AuthHydrateSessionKey
): key is null | typeof AUTH_SESSION_KEY_NONE {
    return key === null || key === AUTH_SESSION_KEY_NONE
}

/**
 * True when the key identifies a real signed-in account (`${userId}:account`).
 *
 * False for pending (`null`), the none sentinel (`'__none__'`), and anonymous fingerprints
 * (`${userId}:anon`). Prefer this over {@link isAbsentAuthSessionKey} for account-only features
 * (e.g. recent messages) when the key may still include anonymous sessions via
 * {@link UseAuthSessionKeyOptions.includeAnonymous}.
 */
export function isAccountAuthSessionKey(key: AuthHydrateSessionKey): key is AuthSessionKey {
    return typeof key === 'string' && key.endsWith(AUTH_SESSION_KEY_ACCOUNT_SUFFIX)
}

/**
 * Derives a session fingerprint from a Better Auth session snapshot.
 *
 * When {@link UseAuthSessionKeyOptions.includeAnonymous} is true, this matches:
 *
 * ```ts
 * if (!session || session.isPending) return null
 * const u = session.data?.user
 * if (!u?.id) return '__none__'
 * return `${u.id}:${u.isAnonymous === true ? 'anon' : 'account'}`
 * ```
 */
export function resolveAuthSessionKey(
    session: AuthSessionSnapshot,
    options: UseAuthSessionKeyOptions = {},
    isLoggedIn = false
): AuthHydrateSessionKey {
    const { includeAnonymous = false } = options

    if (!session || session.isPending) {
        return null
    }

    const user = session.data?.user
    if (!user?.id) {
        return AUTH_SESSION_KEY_NONE
    }

    if (includeAnonymous) {
        return `${user.id}${
            user.isAnonymous === true
                ? AUTH_SESSION_KEY_ANON_SUFFIX
                : AUTH_SESSION_KEY_ACCOUNT_SUFFIX
        }`
    }

    if (!isLoggedIn) {
        return AUTH_SESSION_KEY_NONE
    }

    return `${user.id}${AUTH_SESSION_KEY_ACCOUNT_SUFFIX}`
}
